import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CaretRightOutlined, ClockCircleOutlined, StopOutlined } from '@ant-design/icons';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { defaultValidateDateString, defaultWorkTimeResetDate } from 'src/components/common/common';
import { calculateLotQty, useKSK2022_R01Store } from '../Store_KSK2022_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { KSK2022_R01WorkTimeProcessType } from '../Dto_KSK2022_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useDescText } from 'src/hooks/useDescText';
import { DESC_BTN, DESC_FLD } from 'src/utils/descLanguage/descLanguageUtil';

interface Props {
	handleWorkTimeClick: (productOrderNo: string, processType: KSK2022_R01WorkTimeProcessType, manualTime?: Date, rawTime?: { month: string, day: string, hour: string, minute: string }) => Promise<boolean>;
}

const MODAL_SIZE_STORAGE_KEY = 'KSK2022_R01-work-time-modal-size';

const rowLabelClass = 'flex h-[42px] w-[120px] min-w-[120px] shrink-0 items-center rounded-[8px] border border-[#d4dfed] bg-[#e8eff8] px-[12px] text-left text-[15px] font-bold leading-none whitespace-nowrap text-[#183153]';

const timeInputWrapperOption = {
	wrapperStyle: '!h-[42px] w-[52px] min-w-[52px] mb-0',
	titleStyle: 'hidden',
	inputStyle: 'w-[52px] min-w-[52px] [&>input]:!h-[42px] [&>input]:!rounded-[8px] [&>input]:!border-[#c5d5e9] [&>input]:!px-[8px] [&>input]:text-center [&>input]:text-[17px]',
};

const displayFieldWrapperOption = (width: string) => ({
	wrapperStyle: `!h-[42px] ${width} mb-0`,
	titleStyle: 'hidden',
	inputStyle: 'w-full min-w-0 [&>input]:!h-[42px] [&>input]:!rounded-[8px] [&>input]:!border-[#c5d5e9] [&>input]:!px-[10px] [&>input]:text-[16px]',
});

export const WorkTimeModal_KSK2022_R01 = ({
	handleWorkTimeClick
}: Props) => {
	const { renderDescText, getFormTitleWidth } = useDescText();
	// 공통코드 Store에서 작업조와 작업 시작(WORKTIME_FR)·종료(WORKTIME_TO) 시간 목록을 가져온다.
	const { jobTeamList, workTimeStartList, workTimeEndList } = useCommonCodeStore();
	const {
		workTimeModalVisible, setWorkTimeModalVisible,
		workTimeModalType,
		editKSK2022_R01,
		KSK2022_R01LotList,
		deletedKSK2022_R01LotList,
	} = useKSK2022_R01Store();

	const [month, setMonth] = useState<string>('');
	const [day, setDay] = useState<string>('');
	const [hour, setHour] = useState<string>('');
	const [minute, setMinute] = useState<string>('');
	// 공통코드 시간 버튼의 선택 상태를 표시하며, 시간을 직접 입력하면 선택 상태를 해제한다.
	const [selectedTimeCode, setSelectedTimeCode] = useState<string>('');

	const isStartModal = workTimeModalType === 'start';
	const startDateTimeLabel = renderDescText(DESC_FLD.START_DATETIME, '시작일시');
	const endTimeLabel = renderDescText(DESC_FLD.END_TIME, '종료시간');
	const workTeamLabel = renderDescText(DESC_FLD.WORK_TEAM, '작업팀');
	const workerLabel = renderDescText(DESC_FLD.WORKER, '작업자');
	const productionQtyLabel = renderDescText(DESC_FLD.PRODUCTION_QTY, '생산량');
	const monthLabel = renderDescText(DESC_FLD.MONTH, '월');
	const dayLabel = renderDescText(DESC_FLD.DAY, '일');
	const hourLabel = renderDescText(DESC_FLD.HOUR, '시');
	const minuteLabel = renderDescText(DESC_FLD.MINUTE, '분');
	const rowLabelWidth = useMemo(() => getFormTitleWidth([
		startDateTimeLabel,
		endTimeLabel,
		workTeamLabel,
		workerLabel,
		productionQtyLabel,
	], 120, 200), [endTimeLabel, getFormTitleWidth, productionQtyLabel, startDateTimeLabel, workTeamLabel, workerLabel]);
	const timeUnitExpansion = useMemo(() => {
		const translatedWidth = [monthLabel, dayLabel, hourLabel, minuteLabel].reduce(
			(total, label) => total + getFormTitleWidth([label], 0, 120),
			0,
		);
		const sourceWidth = ['월', '일', '시', '분'].reduce(
			(total, label) => total + getFormTitleWidth([label], 0, 120),
			0,
		);
		return Math.max(0, translatedWidth - sourceWidth);
	}, [dayLabel, getFormTitleWidth, hourLabel, minuteLabel, monthLabel]);
	const modalWidth = Math.min(900, 620 + (rowLabelWidth - 120) + timeUnitExpansion);
	const rowLabelStyle = { width: rowLabelWidth, minWidth: rowLabelWidth };

	const jobTeamLabel = useMemo(() => {
		const jobTeam = editKSK2022_R01?.jobTeam;
		if (!jobTeam) return '';
		return jobTeamList.find(value => value.minorCd === jobTeam)?.codeNm ?? jobTeam;
	}, [editKSK2022_R01?.jobTeam, jobTeamList]);

	const totalLotQty = useMemo(
		() => calculateLotQty(KSK2022_R01LotList, deletedKSK2022_R01LotList),
		[KSK2022_R01LotList, deletedKSK2022_R01LotList],
	);
	// 공통코드의 참조1(시), 참조2(분)를 화면과 Date 객체에서 사용할 수 있는 값으로 변환한다.
	// 범위를 벗어난 참조값은 선택 항목에서 제외하기 위해 null을 반환한다.
	const getWorkTimeOptionValue = useCallback((option: (typeof workTimeStartList)[number]) => {
		const optionHour = Number(option.ref01);
		const optionMinute = Number(option.ref02);
		if (!Number.isInteger(optionHour) || optionHour < 0 || optionHour > 23
			|| !Number.isInteger(optionMinute) || optionMinute < 0 || optionMinute > 59) {
			return null;
		}

		return {
			hour: optionHour,
			minute: optionMinute,
			hourText: optionHour.toString().padStart(2, '0'),
			minuteText: optionMinute.toString().padStart(2, '0'),
		};
	}, []);
	// 현재 모달 종류에 맞는 공통코드만 사용하고, 미사용 코드와 잘못된 시간 값은 제외한다.
	const currentTimeOptions = useMemo(
		() => (isStartModal ? workTimeStartList : workTimeEndList)
			.filter(option => option.sysFlg !== 'N' && getWorkTimeOptionValue(option) !== null),
		[getWorkTimeOptionValue, isStartModal, workTimeEndList, workTimeStartList],
	);
	const productionQtyDisplay = isStartModal
		? (editKSK2022_R01?.pendingLotQty ?? 0)
		: totalLotQty;

	// 공통코드 버튼을 선택하면 해당 시·분을 입력값에 반영한다.
	const handleTimeOptionClick = useCallback((option: (typeof currentTimeOptions)[number]) => {
		const optionValue = getWorkTimeOptionValue(option);
		if (!optionValue) return;

		setHour(optionValue.hourText);
		setMinute(optionValue.minuteText);
		setSelectedTimeCode(option.minorCd);

		// 종료시간이 시작시간과 같거나 빠르면 야간 작업으로 보고 종료일을 다음 날로 보정한다.
		if (!isStartModal && month && day) {
			const year = new Date().getFullYear();
			const selectedDate = new Date(year, Number(month) - 1, Number(day), optionValue.hour, optionValue.minute);
			const workStartDate = editKSK2022_R01?.workStartDt
				? new Date(editKSK2022_R01.workStartDt)
				: null;

			if (workStartDate && !isNaN(workStartDate.getTime()) && selectedDate <= workStartDate) {
				selectedDate.setDate(selectedDate.getDate() + 1);
				setMonth((selectedDate.getMonth() + 1).toString().padStart(2, '0'));
				setDay(selectedDate.getDate().toString().padStart(2, '0'));
			}
		}
	}, [day, editKSK2022_R01?.workStartDt, getWorkTimeOptionValue, isStartModal, month]);

	useEffect(() => {
		if (workTimeModalVisible) {
			const pad = (n: number) => n.toString().padStart(2, '0');
			const now = new Date();
			setMonth(pad(now.getMonth() + 1));
			setDay(pad(now.getDate()));

			const hasProductOrderNo = !!editKSK2022_R01?.productOrderNo;
			const time = isStartModal ? editKSK2022_R01?.workStartDt : editKSK2022_R01?.workEndDt;
			const d = time ? new Date(time) : null;
			const isValidExisting =
				hasProductOrderNo &&
				!!d &&
				!isNaN(d.getTime()) &&
				d.getTime() !== new Date(defaultValidateDateString).getTime() &&
				d.getTime() !== defaultWorkTimeResetDate;

			if (isValidExisting) {
				// 저장된 작업시간이 있으면 저장값을 우선하며, 동일한 공통코드 버튼도 선택 상태로 표시한다.
				setHour(pad(d.getHours()));
				setMinute(pad(d.getMinutes()));
				const matchedOption = currentTimeOptions.find(option => {
					const optionValue = getWorkTimeOptionValue(option);
					return optionValue?.hour === d.getHours() && optionValue.minute === d.getMinutes();
				});
				setSelectedTimeCode(matchedOption?.minorCd ?? '');
			} else {
				// 저장값이 없으면 참조3이 Y인 공통코드를 기본 시간으로 사용한다.
				const defaultOption = currentTimeOptions.find(option => option.ref03?.trim().toUpperCase() === 'Y');
				const defaultOptionValue = defaultOption ? getWorkTimeOptionValue(defaultOption) : null;
				if (defaultOption && defaultOptionValue) {
					setHour(defaultOptionValue.hourText);
					setMinute(defaultOptionValue.minuteText);
					setSelectedTimeCode(defaultOption.minorCd);

					if (!isStartModal) {
						const workStartDate = editKSK2022_R01?.workStartDt
							? new Date(editKSK2022_R01.workStartDt)
							: null;
						const selectedDate = new Date(
							now.getFullYear(),
							now.getMonth(),
							now.getDate(),
							defaultOptionValue.hour,
							defaultOptionValue.minute,
						);
						if (workStartDate && !isNaN(workStartDate.getTime()) && selectedDate <= workStartDate) {
							selectedDate.setDate(selectedDate.getDate() + 1);
							setMonth(pad(selectedDate.getMonth() + 1));
							setDay(pad(selectedDate.getDate()));
						}
					}
				} else {
					// 기본 공통코드가 지정되지 않은 경우에는 브라우저의 현재 시각을 사용한다.
					setHour(pad(now.getHours()));
					setMinute(pad(now.getMinutes()));
					setSelectedTimeCode('');
				}
			}
		}
	}, [currentTimeOptions, editKSK2022_R01, getWorkTimeOptionValue, isStartModal, workTimeModalVisible]);

	const handleModify = async () => {
		if (!month || !day || !hour || !minute) {
			showWarning('시간을 모두 입력해주세요.');
			return;
		}

		const year = new Date().getFullYear();
        // const newTime = new Date(year, Number(month) - 1, Number(day), Number(hour) + 9, Number(minute));
		const newTime = new Date(year, Number(month) - 1, Number(day), Number(hour), Number(minute));
		const rawTime = { month, day, hour, minute };
		const productOrderNo = editKSK2022_R01?.productOrderNo || '';

		const success = await handleWorkTimeClick(
			productOrderNo,
			isStartModal ? 'workStart' : 'workEnd',
			newTime,
			rawTime,
		);
		if (success) {
			setWorkTimeModalVisible(false);
		}
	};

	const handleReset = async () => {
		const productOrderNo = editKSK2022_R01?.productOrderNo || '';
		const success = await handleWorkTimeClick(
			productOrderNo,
			isStartModal ? 'workStartReset' : 'workEndReset',
		);
		if (success) {
			setWorkTimeModalVisible(false);
		}
	};

	const handleMinuteKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			void handleModify();
		}
	}, [month, day, hour, minute, isStartModal, editKSK2022_R01, handleWorkTimeClick, setWorkTimeModalVisible]);

	if (!workTimeModalVisible) return null;

	return (
		<ModalWrapper
			size="hsm"
			defaultWidth={modalWidth}
			sizeStorageKey={MODAL_SIZE_STORAGE_KEY}
			overlay={true}
			onOverlayClick={() => setWorkTimeModalVisible(false)}
			onEscKeyDown={() => setWorkTimeModalVisible(false)}
		>
			<div className="flex h-full min-h-[360px] flex-col overflow-hidden rounded-[6px] bg-[#f5f8fc]">
			<div className="flex h-[58px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[22px] text-white">
				{isStartModal ? <CaretRightOutlined className="text-[21px]" /> : <StopOutlined className="text-[19px]" />}
				<p className="text-[21px] font-bold">
					{isStartModal
						? renderDescText(DESC_FLD.WORK_START, '작업시작')
						: renderDescText(DESC_FLD.WORK_END, '작업종료')}
				</p>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-[10px] overflow-y-auto p-[18px]">
				<div className="flex flex-col gap-[9px] rounded-[10px] border border-[#d8e3f0] bg-white p-[10px] shadow-sm">
					<div className="flex items-center gap-[7px]">
					<span className={rowLabelClass} style={rowLabelStyle}>
						<ClockCircleOutlined className="mr-[7px]" />
						{isStartModal ? startDateTimeLabel : endTimeLabel}
					</span>
					<InputForm
						title={monthLabel}
						name="month"
						value={month}
						onChange={(e) => setMonth(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span className="text-[16px] font-medium whitespace-nowrap text-[#263b57]">{monthLabel}</span>
					<InputForm
						title={dayLabel}
						name="day"
						value={day}
						onChange={(e) => setDay(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span className="text-[16px] font-medium whitespace-nowrap text-[#263b57]">{dayLabel}</span>
					<InputForm
						title={hourLabel}
						name="hour"
						value={hour}
						onChange={(e) => {
							setHour(e.target.value);
							setSelectedTimeCode('');
						}}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span className="text-[16px] font-medium whitespace-nowrap text-[#263b57]">{hourLabel}</span>
					<InputForm
						title={minuteLabel}
						name="minute"
						value={minute}
						onChange={(e) => {
							setMinute(e.target.value);
							setSelectedTimeCode('');
						}}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
						onKeyDown={handleMinuteKeyDown}
					/>
					<span className="text-[16px] font-medium whitespace-nowrap text-[#263b57]">{minuteLabel}</span>
					</div>
					{/* 공통코드의 24시간 값을 오전·오후 표기와 함께 빠른 선택 버튼으로 표시한다. */}
					{currentTimeOptions.length > 0 && (
						<div className="flex flex-wrap items-center gap-[7px] pl-[127px]" style={{ paddingLeft: rowLabelWidth + 7 }}>
							{currentTimeOptions.map(option => {
								const optionValue = getWorkTimeOptionValue(option);
								if (!optionValue) return null;
								const periodLabel = optionValue.hour < 12 ? '오전' : '오후';
								const isSelected = selectedTimeCode === option.minorCd;

								return (
									<button
										type="button"
										key={option.minorCd}
										title={option.codeNm}
										className={`h-[36px] min-w-[104px] rounded-[8px] border px-[12px] text-[14px] font-bold shadow-sm ${isSelected
											? 'border-[#2359cf] bg-[#285dd7] text-white'
											: 'border-[#c5d5e9] bg-[#f4f7fb] text-[#27405f] hover:bg-[#e8eff8]'}`}
										onClick={() => handleTimeOptionClick(option)}
									>
										{periodLabel} {optionValue.hourText}:{optionValue.minuteText}
									</button>
								);
							})}
						</div>
					)}
				</div>

				<div className="flex items-center gap-[7px]">
					<span className={rowLabelClass} style={rowLabelStyle}>{workTeamLabel}</span>
					<InputForm
						title={workTeamLabel}
						name="jobTeamDisplay"
						value={jobTeamLabel}
						editable={false}
						wrapperOption={displayFieldWrapperOption('flex-1 min-w-0')}
						titleless={true}
					/>
				</div>

				<div className="flex items-center gap-[7px]">
					<span className={rowLabelClass} style={rowLabelStyle}>{workerLabel}</span>
					<InputForm
						title={renderDescText(DESC_FLD.EMPLOYEE_NO, '작업자코드')}
						name="orderEmpDisplay"
						value={editKSK2022_R01?.orderEmp || ''}
						editable={false}
						wrapperOption={displayFieldWrapperOption('w-[150px] min-w-[150px]')}
						titleless={true}
					/>
					<InputForm
						title={renderDescText(DESC_FLD.EMPLOYEE_NAME, '작업자명')}
						name="orderEmpNmDisplay"
						value={editKSK2022_R01?.orderEmpNm || ''}
						editable={false}
						wrapperOption={displayFieldWrapperOption('flex-1 min-w-0')}
						titleless={true}
					/>
				</div>

				<div className="flex items-center gap-[7px]">
					<span className={rowLabelClass} style={rowLabelStyle}>{productionQtyLabel}</span>
					<InputForm
						title={productionQtyLabel}
						name="productionQtyDisplay"
						value={productionQtyDisplay}
						editable={false}
						wrapperOption={displayFieldWrapperOption('flex-1 min-w-0')}
						titleless={true}
					/>
				</div>
			</div>

			<div className="flex shrink-0 items-center justify-end gap-[8px] border-t border-[#d8e3f0] bg-white px-[18px] py-[12px]">
				<button
					className="h-[40px] min-w-[82px] flex-shrink-0 rounded-[8px] border border-[#bdcadd] bg-[#eef2f7] px-[16px] text-[15px] font-bold text-[#27405f] shadow-sm hover:bg-[#e2e9f2]"
					onClick={handleReset}
				>
					{renderDescText(DESC_BTN.RESET, '초기화')}
				</button>
				<button
					className="h-[40px] min-w-[82px] flex-shrink-0 rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[16px] text-[15px] font-bold text-white shadow-md hover:brightness-105"
					onClick={handleModify}
				>
					{renderDescText(DESC_BTN.CONFIRM, '확인')}
				</button>
				<button
					className="h-[40px] min-w-[82px] flex-shrink-0 rounded-[8px] border border-[#bdcadd] bg-white px-[16px] text-[15px] font-bold text-[#52657d] shadow-sm hover:bg-[#eef2f7]"
					onClick={() => setWorkTimeModalVisible(false)}
				>
					{renderDescText(DESC_BTN.CANCEL, '취소')}
				</button>
			</div>
			</div>
		</ModalWrapper>
	);
};
